import { ReactNode } from 'react'
import FundsIcon from 'assets/images/funds/moneypool-2.png'
import FundsIcon2 from 'assets/images/funds/moneyPool-1.svg'
import { MoneyPoolsStatus } from 'utilities/helper/moneyPools'
import { useNavigate } from 'react-router-dom'
import { ConvertJalaliDate } from 'utilities/helper/digitToMonth'
import { faPlain } from 'utilities/helper/format'
import posthog from 'posthog-js'
import {
    Amount,
    Chip,
    FundCard,
    FundCardHeader,
    FundPayButton,
    FundRow,
    FundSection,
    StatusLine,
} from '../../card'

interface IItem {
    item: MoneyPool
    /** The reorder grip, when the list is long enough to be sortable. */
    dragHandle?: ReactNode
    /** Whether a search box was filtering this list when the card was tapped. */
    fromSearch?: boolean
    rowIndex?: number
}

// An unmapped status used to crash the whole tab: the lookup was indexed
// straight into and then dereferenced, so one unrecognised state from the API
// took out every card on the screen, not just its own chip.
const UNKNOWN_STATUS = {
    title: 'نامشخص',
    icon: '',
    color: '#646E7A',
    bgColor: '#F0F2F5',
}
const statusOf = (key: string) => MoneyPoolsStatus[key] ?? UNKNOWN_STATUS

export default function Item({
    item,
    dragHandle,
    fromSearch,
    rowIndex,
}: IItem) {
    const navigate = useNavigate()

    const showLoanStatus =
        item.poolship.active_installments_status !== 'no-loan' &&
        item.poolship.active_installments_status !== 'fully-repaid'

    // Android BoxListAdapter: btnPay is visible while the member still owes
    // (share or loan unpaid/delayed), hidden once everything is paid.
    const owesShare =
        item.poolship.portion_payment_status === 'ps_unpaid' ||
        item.poolship.portion_payment_status === 'ps_delayed'
    const owesLoan =
        showLoanStatus &&
        item.poolship.active_installments_status !== 'paid' &&
        item.poolship.active_installments_status !== 'ps_paid'
    const showPayButton = owesShare || owesLoan

    const fallbackIcon = item.image_id === 2 ? FundsIcon : FundsIcon2
    const portion = statusOf(item.poolship.portion_payment_status)
    const installments = statusOf(item.poolship.active_installments_status)

    return (
        <FundCard
            onClick={() => {
                // `fund_id` only. The name is written by the fund's own members
                // — «صندوق خانوادگی رضایی‌ها» — so it is user-authored personal
                // data, and shipping it to a third party buys nothing that the
                // id does not already answer.
                // `from_search` is the search's success event: a member who
                // types a name and then opens a card got what they came for,
                // and nothing else can observe that.
                posthog.capture('fund_list_item_clicked', {
                    fund_id: item.id,
                    from_search: !!fromSearch,
                    row_index: rowIndex,
                })
                navigate(`/fund/${item.id}`, {
                    state: { poolShipId: item.poolship.id },
                })
            }}
        >
            <FundCardHeader
                avatar={item.custom_avatar ? item.custom_avatar : fallbackIcon}
                title={`صندوق ${item.name}`}
                subtitle={`${faPlain.format(
                    item.active_members_count
                )} عضو · پس‌انداز`}
                onAvatarError={(event) => {
                    event.currentTarget.onerror = null
                    event.currentTarget.src = fallbackIcon
                }}
                dragHandle={dragHandle}
            />

            <FundSection>
                <div className="flex flex-col gap-2">
                    <FundRow
                        label="موجودی صندوق"
                        strong
                        value={
                            item.show_box_balance ? (
                                <Amount
                                    value={
                                        Number(item.hamyan_balance) +
                                        Number(item.bank_balance)
                                    }
                                />
                            ) : (
                                '—'
                            )
                        }
                    />
                    {Number(item.share_value_amount) !== 0 && (
                        <FundRow
                            label="حق عضویت"
                            value={<Amount value={item.share_value_amount} />}
                        />
                    )}
                    <FundRow
                        label="تاریخ سررسید"
                        value={ConvertJalaliDate(item.jalali_due_date)}
                    />
                </div>
            </FundSection>

            <FundSection>
                <p className="text-sm text-text-secondary">وضعیت پرداخت دوره</p>
                <div className="mt-2 flex flex-col gap-2">
                    <StatusLine label="حق عضویت">
                        <Chip
                            bg={portion.bgColor}
                            color={portion.color}
                            icon={portion.icon || undefined}
                            label={portion.title}
                        />
                    </StatusLine>
                    {showLoanStatus && (
                        <StatusLine label="قسط وام">
                            <Chip
                                bg={installments.bgColor}
                                color={installments.color}
                                icon={installments.icon || undefined}
                                label={installments.title}
                            />
                        </StatusLine>
                    )}
                </div>
            </FundSection>

            {showPayButton && (
                <FundPayButton
                    onClick={() => {
                        posthog.capture('payables_opened', {
                            source: 'fund_card_pay',
                        })
                        navigate('/easy-pay')
                    }}
                />
            )}
        </FundCard>
    )
}
